/**
 * DriverRidesView.js - Dedicated Driver Interface for Ride Pickups
 * Features: Available rides, GPS navigation with Leaflet map, ride status management, job completion with photos & signatures
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Car, Navigation, MapPin, Clock, DollarSign, Phone, MessageSquare,
  Play, CheckCircle, XCircle, RefreshCw, User, AlertCircle,
  ChevronRight, Compass, Timer, Route, Flag, Camera, Map, List
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { ridesAPI } from '../../services/api';
import JobCompletionModal from './JobCompletionModal';

const RIDE_STATUSES = {
  requested: { label: 'Requested', color: 'bg-yellow-500', next: 'Accept Ride' },
  driver_assigned: { label: 'Assigned', color: 'bg-blue-500', next: 'Start Navigation' },
  en_route_pickup: { label: 'En Route to Pickup', color: 'bg-purple-500', next: 'Arrived at Pickup' },
  arrived_pickup: { label: 'At Pickup', color: 'bg-orange-500', next: 'Start Ride' },
  in_progress: { label: 'In Progress', color: 'bg-green-500', next: 'Complete Ride' },
  completed: { label: 'Completed', color: 'bg-gray-500', next: null }
};

const DriverRidesView = ({ onRideComplete }) => {
  const [activeTab, setActiveTab] = useState('available');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [availableRides, setAvailableRides] = useState([]);
  const [myRides, setMyRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRide, setSelectedRide] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [driverLocation, setDriverLocation] = useState(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completingRide, setCompletingRide] = useState(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  // Get driver's current location
  useEffect(() => {
    if (navigator.geolocation) {
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          setDriverLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => console.error('GPS Error:', error),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
      );
      return () => navigator.geolocation.clearWatch(watchId);
    }
  }, []);

  const fetchRides = useCallback(async () => {
    setLoading(true);
    try {
      const [availableRes, myRidesRes] = await Promise.all([
        ridesAPI.getJobBoard().catch(() => ({ data: { available_rides: [] } })),
        ridesAPI.getAll({ status: 'driver_assigned,en_route_pickup,arrived_pickup,in_progress' }).catch(() => ({ data: [] }))
      ]);
      
      setAvailableRides(availableRes.data?.available_rides || []);
      setMyRides(Array.isArray(myRidesRes.data) ? myRidesRes.data : []);
    } catch (error) {
      console.error('Failed to fetch rides:', error);
      toast.error('Failed to load rides');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRides();
    const interval = setInterval(fetchRides, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [fetchRides]);

  // Initialize Leaflet map when in map view
  useEffect(() => {
    if (viewMode !== 'map' || !mapContainerRef.current) return;
    
    // Load Leaflet dynamically
    const loadLeaflet = async () => {
      // Add Leaflet CSS if not present
      if (!document.getElementById('leaflet-css')) {
        const link = document.createElement('link');
        link.id = 'leaflet-css';
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }
      
      // Load Leaflet JS
      if (!window.L) {
        await new Promise((resolve) => {
          const script = document.createElement('script');
          script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
          script.onload = resolve;
          document.head.appendChild(script);
        });
      }
      
      // Wait for CSS to load
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Initialize map
      if (mapContainerRef.current && window.L && !mapRef.current) {
        const defaultCenter = driverLocation 
          ? [driverLocation.lat, driverLocation.lng] 
          : [35.93, -77.79]; // Princeville, NC
        
        mapRef.current = window.L.map(mapContainerRef.current).setView(defaultCenter, 12);
        
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap'
        }).addTo(mapRef.current);
      }
    };
    
    loadLeaflet();
    
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [viewMode]);

  // Update map markers when rides change
  useEffect(() => {
    if (!mapRef.current || !window.L) return;
    
    // Clear existing markers
    mapRef.current.eachLayer(layer => {
      if (layer instanceof window.L.Marker) {
        mapRef.current.removeLayer(layer);
      }
    });
    
    const ridesToShow = activeTab === 'available' ? availableRides : myRides;
    const bounds = [];
    
    // Add driver marker
    if (driverLocation) {
      const driverIcon = window.L.divIcon({
        html: `<div style="background: #22c55e; width: 16px; height: 16px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
        className: 'driver-marker',
        iconSize: [16, 16]
      });
      
      window.L.marker([driverLocation.lat, driverLocation.lng], { icon: driverIcon })
        .addTo(mapRef.current)
        .bindPopup('You are here');
      
      bounds.push([driverLocation.lat, driverLocation.lng]);
    }
    
    // Add ride markers
    ridesToShow.forEach(ride => {
      const pickup = ride.pickup_location || {};
      const dropoff = ride.dropoff_location || {};
      
      if (pickup.lat && pickup.lng) {
        const pickupIcon = window.L.divIcon({
          html: `<div style="background: #22c55e; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 12px; font-weight: bold; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">P</div>`,
          className: 'pickup-marker',
          iconSize: [24, 24]
        });
        
        window.L.marker([pickup.lat, pickup.lng], { icon: pickupIcon })
          .addTo(mapRef.current)
          .bindPopup(`<strong>Pickup:</strong><br/>${pickup.address || 'N/A'}`);
        
        bounds.push([pickup.lat, pickup.lng]);
      }
      
      if (dropoff.lat && dropoff.lng) {
        const dropoffIcon = window.L.divIcon({
          html: `<div style="background: #ef4444; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 12px; font-weight: bold; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">D</div>`,
          className: 'dropoff-marker',
          iconSize: [24, 24]
        });
        
        window.L.marker([dropoff.lat, dropoff.lng], { icon: dropoffIcon })
          .addTo(mapRef.current)
          .bindPopup(`<strong>Dropoff:</strong><br/>${dropoff.address || 'N/A'}`);
        
        bounds.push([dropoff.lat, dropoff.lng]);
      }
    });
    
    // Fit bounds if we have markers
    if (bounds.length > 1) {
      mapRef.current.fitBounds(bounds, { padding: [50, 50] });
    } else if (bounds.length === 1) {
      mapRef.current.setView(bounds[0], 13);
    }
  }, [activeTab, availableRides, myRides, driverLocation]);

  // Navigate to location using Google Maps
  const openNavigation = (address, city, state, zip) => {
    const destination = encodeURIComponent(`${address}, ${city}, ${state} ${zip}`);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${destination}&travelmode=driving`, '_blank');
  };

  // Accept a ride
  const handleAcceptRide = async (ride) => {
    setActionLoading(true);
    try {
      await ridesAPI.accept(ride.id);
      toast.success('Ride accepted!');
      fetchRides();
      setSelectedRide(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept ride');
    } finally {
      setActionLoading(false);
    }
  };

  // Start ride (after pickup)
  const handleStartRide = async (ride) => {
    if (!driverLocation) {
      toast.error('Unable to get your location. Please enable GPS.');
      return;
    }
    
    setActionLoading(true);
    try {
      await ridesAPI.start(ride.id, driverLocation.lat, driverLocation.lng);
      toast.success('Ride started!');
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start ride');
    } finally {
      setActionLoading(false);
    }
  };

  // Complete ride - Opens completion modal
  const handleCompleteRide = async (ride) => {
    if (!driverLocation) {
      toast.error('Unable to get your location. Please enable GPS.');
      return;
    }
    
    // Open completion modal for photos and signature
    setCompletingRide(ride);
    setShowCompletionModal(true);
  };
  
  // Handle completion from modal
  const handleCompletionDone = async (completionData) => {
    if (!completingRide || !driverLocation) return;
    
    setActionLoading(true);
    try {
      const result = await ridesAPI.complete(completingRide.id, driverLocation.lat, driverLocation.lng);
      toast.success(`Ride completed! Earnings: $${result.data.driver_earnings?.toFixed(2) || '0.00'}`);
      fetchRides();
      onRideComplete?.(result.data);
      setCompletingRide(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete ride');
    } finally {
      setActionLoading(false);
    }
  };

  // Quick complete without modal (for rides where photos/signature not required)
  const handleQuickComplete = async (ride) => {
    if (!driverLocation) {
      toast.error('Unable to get your location. Please enable GPS.');
      return;
    }
    
    setActionLoading(true);
    try {
      const result = await ridesAPI.complete(ride.id, driverLocation.lat, driverLocation.lng);
      toast.success(`Ride completed! Earnings: $${result.data.driver_earnings?.toFixed(2) || '0.00'}`);
      fetchRides();
      onRideComplete?.(result.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete ride');
    } finally {
      setActionLoading(false);
    }
  };

  // Format time
  const formatTime = (datetime) => {
    if (!datetime) return 'ASAP';
    try {
      const date = new Date(datetime);
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch {
      return datetime;
    }
  };

  // Ride Card Component
  const RideCard = ({ ride, isAvailable = false }) => {
    const pickup = ride.pickup_location || {};
    const dropoff = ride.dropoff_location || {};
    const statusInfo = RIDE_STATUSES[ride.status] || RIDE_STATUSES.requested;
    const fare = ride.estimated_fare || 0;
    const earnings = ride.driver_earnings || (fare * 0.85);

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-xl overflow-hidden"
        data-testid={`ride-card-${ride.id}`}
      >
        {/* Status Header */}
        <div className={`${statusInfo.color} px-4 py-2 flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <Car className="w-4 h-4 text-white" />
            <span className="text-white font-semibold text-sm uppercase">
              {ride.reference || 'Ride'}
            </span>
          </div>
          <span className="text-white/90 text-xs font-medium">
            {statusInfo.label}
          </span>
        </div>

        <div className="p-4 space-y-3">
          {/* Pickup */}
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <div className="w-3 h-3 rounded-full bg-green-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-[var(--text-muted)] uppercase tracking-wide">Pickup</p>
              <p className="font-medium text-[var(--text-primary)] truncate">
                {pickup.address || 'Address pending'}
              </p>
              <p className="text-sm text-[var(--text-muted)]">
                {pickup.city}, {pickup.state || 'NC'}
              </p>
            </div>
            {!isAvailable && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openNavigation(pickup.address, pickup.city, pickup.state, pickup.zip_code)}
                className="flex-shrink-0"
              >
                <Navigation className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Route Line */}
          <div className="flex items-center gap-3 pl-4">
            <div className="w-0.5 h-6 bg-[var(--border-primary)] ml-3" />
          </div>

          {/* Dropoff */}
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Flag className="w-4 h-4 text-red-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-[var(--text-muted)] uppercase tracking-wide">Dropoff</p>
              <p className="font-medium text-[var(--text-primary)] truncate">
                {dropoff.address || 'Address pending'}
              </p>
              <p className="text-sm text-[var(--text-muted)]">
                {dropoff.city}, {dropoff.state || 'NC'}
              </p>
            </div>
            {!isAvailable && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openNavigation(dropoff.address, dropoff.city, dropoff.state, dropoff.zip_code)}
                className="flex-shrink-0"
              >
                <Navigation className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Customer & Time */}
          <div className="flex items-center justify-between pt-2 border-t border-[var(--border-primary)]">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-[var(--text-muted)]" />
              <span className="text-sm text-[var(--text-secondary)]">
                {ride.customer_name || 'Customer'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[var(--text-muted)]" />
              <span className="text-sm text-[var(--text-secondary)]">
                {ride.is_asap ? 'ASAP' : formatTime(ride.pickup_datetime)}
              </span>
            </div>
          </div>

          {/* Earnings */}
          <div className="bg-[var(--primary)]/10 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[var(--primary)]" />
              <span className="text-[var(--text-muted)]">Your Earnings</span>
            </div>
            <span className="text-xl font-bold text-[var(--primary)]">
              ${earnings.toFixed(2)}
            </span>
          </div>

          {/* Contact Buttons - Only for assigned rides */}
          {!isAvailable && ride.customer_phone && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => window.open(`tel:${ride.customer_phone}`)}
              >
                <Phone className="w-4 h-4 mr-2" />
                Call
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => window.open(`sms:${ride.customer_phone}`)}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Text
              </Button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="pt-2">
            {isAvailable ? (
              <Button
                onClick={() => handleAcceptRide(ride)}
                disabled={actionLoading}
                className="w-full h-12 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-black font-bold"
                data-testid={`accept-ride-${ride.id}`}
              >
                {actionLoading ? 'Accepting...' : 'ACCEPT RIDE'}
              </Button>
            ) : (
              <div className="space-y-2">
                {ride.status === 'driver_assigned' && (
                  <>
                    <Button
                      onClick={() => openNavigation(pickup.address, pickup.city, pickup.state, pickup.zip_code)}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <Navigation className="w-4 h-4 mr-2" />
                      Navigate to Pickup
                    </Button>
                    <Button
                      onClick={() => handleStartRide(ride)}
                      disabled={actionLoading}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`start-ride-${ride.id}`}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      {actionLoading ? 'Starting...' : 'Customer Picked Up - Start Ride'}
                    </Button>
                  </>
                )}
                {ride.status === 'in_progress' && (
                  <>
                    <Button
                      onClick={() => openNavigation(dropoff.address, dropoff.city, dropoff.state, dropoff.zip_code)}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <Navigation className="w-4 h-4 mr-2" />
                      Navigate to Dropoff
                    </Button>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleCompleteRide(ride)}
                        disabled={actionLoading}
                        className="flex-1 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-black font-bold"
                        data-testid={`complete-ride-${ride.id}`}
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        {actionLoading ? 'Completing...' : 'Complete with Proof'}
                      </Button>
                      <Button
                        onClick={() => handleQuickComplete(ride)}
                        disabled={actionLoading}
                        variant="outline"
                        className="border-[var(--primary)] text-[var(--primary)]"
                        data-testid={`quick-complete-${ride.id}`}
                      >
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[var(--background-paper)] border-b border-[var(--border-primary)]">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Car className="w-6 h-6 text-[var(--primary)]" />
              <h1 className="text-xl font-bold text-[var(--text-primary)]">Driver Rides</h1>
            </div>
            <div className="flex items-center gap-2">
              {/* View Mode Toggle */}
              <div className="flex bg-[var(--background)] rounded-lg p-1">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-[var(--primary)] text-black' : 'text-[var(--text-muted)]'}`}
                  data-testid="view-list"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  className={`p-2 rounded ${viewMode === 'map' ? 'bg-[var(--primary)] text-black' : 'text-[var(--text-muted)]'}`}
                  data-testid="view-map"
                >
                  <Map className="w-4 h-4" />
                </button>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchRides}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* GPS Status */}
          <div className={`flex items-center gap-2 text-sm mb-3 ${driverLocation ? 'text-green-500' : 'text-amber-500'}`}>
            <Compass className="w-4 h-4" />
            <span>{driverLocation ? 'GPS Active' : 'Waiting for GPS...'}</span>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-[var(--border-primary)]">
            <button
              onClick={() => setActiveTab('available')}
              className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'available'
                  ? 'border-[var(--primary)] text-[var(--primary)]'
                  : 'border-transparent text-[var(--text-muted)]'
              }`}
              data-testid="tab-available"
            >
              Available ({availableRides.length})
            </button>
            <button
              onClick={() => setActiveTab('active')}
              className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'active'
                  ? 'border-[var(--primary)] text-[var(--primary)]'
                  : 'border-transparent text-[var(--text-muted)]'
              }`}
              data-testid="tab-active"
            >
              My Rides ({myRides.length})
            </button>
          </div>
        </div>
      </div>

      {/* Map View */}
      {viewMode === 'map' && (
        <div className="relative">
          <div
            ref={mapContainerRef}
            className="h-[50vh] w-full"
            data-testid="driver-map"
          />
          {/* Map Legend */}
          <div className="absolute bottom-4 left-4 bg-[var(--background-paper)] p-2 rounded-lg shadow-lg text-xs">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-[var(--text-muted)]">Your Location / Pickup</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-[var(--text-muted)]">Dropoff</span>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-4 pb-24 space-y-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-10 h-10 border-3 border-[var(--primary)] border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-[var(--text-muted)]">Loading rides...</p>
          </div>
        ) : activeTab === 'available' ? (
          availableRides.length === 0 ? (
            <div className="text-center py-12">
              <Car className="w-16 h-16 mx-auto mb-4 text-[var(--text-muted)] opacity-50" />
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">No Rides Available</h3>
              <p className="text-[var(--text-muted)]">Check back soon for new ride requests!</p>
            </div>
          ) : (
            <AnimatePresence>
              {availableRides.map(ride => (
                <RideCard key={ride.id} ride={ride} isAvailable={true} />
              ))}
            </AnimatePresence>
          )
        ) : (
          myRides.length === 0 ? (
            <div className="text-center py-12">
              <Route className="w-16 h-16 mx-auto mb-4 text-[var(--text-muted)] opacity-50" />
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">No Active Rides</h3>
              <p className="text-[var(--text-muted)]">Accept a ride from the Available tab to get started!</p>
            </div>
          ) : (
            <AnimatePresence>
              {myRides.map(ride => (
                <RideCard key={ride.id} ride={ride} isAvailable={false} />
              ))}
            </AnimatePresence>
          )
        )}
      </div>
      
      {/* Job Completion Modal */}
      <JobCompletionModal
        isOpen={showCompletionModal}
        onClose={() => {
          setShowCompletionModal(false);
          setCompletingRide(null);
        }}
        job={completingRide}
        serviceType="rides"
        onComplete={handleCompletionDone}
        requireSignature={true}
        requirePhoto={true}
      />
    </div>
  );
};

export default DriverRidesView;
